import { useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCw, Search, CheckCircle2, AlertTriangle, XCircle, LockOpen } from 'lucide-react';
import { toast } from 'sonner';

const CHECKS = [
  { key: 'domain_ownership', title: 'Domain-Zugehörigkeit' },
  { key: 'dns', title: 'DNS-Check (Kundendomain)' },
  { key: 'webmail_reachable', title: 'Webmail erreichbar' },
  { key: 'ssl', title: 'SSL-Check' },
  { key: 'fail2ban', title: 'Fail2Ban-Check' },
  { key: 'nginx_logs', title: 'Nginx-Log-Check (10 Min)' },
  { key: 'ports', title: 'Port-Check (993/465/587)' },
  { key: 'auth_backend', title: 'Auth-Backend-Check (9080)' },
];

const getStatusMeta = (status) => {
  if (status === 'green') {
    return {
      label: 'OK',
      icon: CheckCircle2,
      badgeClass: 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/20',
      cardClass: 'border-[#10B981]/20 bg-[#10B981]/[0.03]',
    };
  }
  if (status === 'red') {
    return {
      label: 'Fehler',
      icon: XCircle,
      badgeClass: 'bg-[#F43F5E]/15 text-[#F43F5E] border-[#F43F5E]/20',
      cardClass: 'border-[#F43F5E]/20 bg-[#F43F5E]/[0.03]',
    };
  }
  return {
    label: 'Hinweis',
    icon: AlertTriangle,
    badgeClass: 'bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/20',
    cardClass: 'border-[#F59E0B]/20 bg-[#F59E0B]/[0.03]',
  };
};

const safeStringify = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export function MailTroubleshooterWidget() {
  const { authAxios } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [unbanReason, setUnbanReason] = useState('');
  const [unbanLoading, setUnbanLoading] = useState(false);

  const checks = useMemo(() => {
    const raw = result?.checks || {};
    const ownership = raw?.domain_ownership?.details?.ownership;
    const visibleChecks = ownership === 'external'
      ? [{ key: 'domain_ownership', title: 'Domain-Zugehörigkeit' }]
      : CHECKS;

    return visibleChecks.map((c) => {
      const data = raw[c.key] || {};
      return {
        key: c.key,
        title: c.title,
        status: data.status || 'yellow',
        summary: data.summary || '',
        details: data.details || {},
      };
    });
  }, [result]);

  const derivedIp = result?.derived?.ip || null;
  const fail2banStatus = result?.checks?.fail2ban?.status;
  const canUnban = fail2banStatus === 'red' && Boolean(derivedIp);

  const runTroubleshoot = async () => {
    const value = query.trim();
    if (!value) return;
    setLoading(true);
    setResult(null);
    setUnbanReason('');

    try {
      const api = authAxios();
      const res = await api.post('/mail/troubleshoot', { query: value });
      setResult(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Fehler bei der Diagnose.';
      toast.error(msg);
      setResult({
        summary: { overall: 'red' },
        checks: {},
        error: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const runUnban = async () => {
    if (!derivedIp) return;
    setUnbanLoading(true);
    try {
      const api = authAxios();
      await api.post('/mail/unban', { ip: derivedIp, confirm: true, reason: unbanReason || undefined });
      toast.success(`IP entsperrt: ${derivedIp}`);
      await runTroubleshoot();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Entsperren fehlgeschlagen.';
      toast.error(msg);
    } finally {
      setUnbanLoading(false);
    }
  };

  return (
    <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-white">Mail Troubleshooter</h3>
            <p className="text-sm text-[#a1a1aa] mt-1">
              Diagnose für <span className="text-white">E-Mail</span> oder <span className="text-white">Client-IP</span> (Proxy: mail.fyremail.de)
            </p>
          </div>
          {result?.summary?.overall && (
            <Badge className={`${getStatusMeta(result.summary.overall).badgeClass} border`}>
              {getStatusMeta(result.summary.overall).label}
            </Badge>
          )}
        </div>

        <div className="mt-5 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !loading && runTroubleshoot()}
              placeholder="E-Mail (z.B. info@kunde.de) oder IP (z.B. 84.150.12.34)"
              className="h-11 pl-10 bg-[#0B0B0C] border-white/10 focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C] text-white placeholder:text-[#52525b] rounded-lg"
            />
          </div>
          <Button
            onClick={runTroubleshoot}
            disabled={loading || !query.trim()}
            className="h-11 px-6 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg shadow-[0_0_15px_rgba(239,105,76,0.3)] hover:shadow-[0_0_25px_rgba(239,105,76,0.5)] transition-all duration-200 active:scale-95"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Analysiere…' : 'Analysieren'}
          </Button>
        </div>

        {result?.error && (
          <div className="mt-4 p-4 bg-[#F43F5E]/10 border border-[#F43F5E]/20 rounded-lg text-[#F43F5E] text-sm">
            {result.error}
          </div>
        )}

        {result && !loading && (
          <div className="mt-6 space-y-4">
            {derivedIp && (
              <div className="text-xs text-[#a1a1aa]">
                Abgeleitete IP: <span className="text-white font-medium">{derivedIp}</span>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {checks.map((c) => {
                const meta = getStatusMeta(c.status);
                const Icon = meta.icon;
                const detailsText = safeStringify(c.details);
                const isFail2Ban = c.key === 'fail2ban';

                return (
                  <div
                    key={c.key}
                    className={`rounded-xl border ${meta.cardClass} p-4`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" strokeWidth={2} />
                          <div className="text-white font-semibold text-sm truncate">{c.title}</div>
                        </div>
                        <div className="text-sm text-[#a1a1aa] mt-1">{c.summary || '—'}</div>
                      </div>
                      <Badge className={`${meta.badgeClass} border whitespace-nowrap`}>{meta.label}</Badge>
                    </div>

                    <div className="mt-3">
                      <Accordion type="single" collapsible>
                        <AccordionItem value="details" className="border-0">
                          <AccordionTrigger className="text-xs text-[#a1a1aa] hover:text-white py-2">
                            Details anzeigen
                          </AccordionTrigger>
                          <AccordionContent>
                            <pre className="text-xs text-[#a1a1aa] bg-black/40 border border-white/10 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                              {detailsText}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>

                    {isFail2Ban && canUnban && (
                      <div className="mt-4">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              disabled={unbanLoading}
                              className="h-10 px-4 rounded-lg font-semibold"
                            >
                              <LockOpen className="w-4 h-4 mr-2" />
                              IP entsperren
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#121214] border-white/10 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>IP entsperren?</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#a1a1aa]">
                                Diese Aktion führt <span className="text-white">fail2ban-client unban</span> auf dem Mail-Proxy aus.
                                Bitte nur nach ausdrücklicher Bestätigung.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div className="space-y-3">
                              <div className="text-sm">
                                Ziel-IP: <span className="font-semibold">{derivedIp}</span>
                              </div>
                              <Textarea
                                value={unbanReason}
                                onChange={(e) => setUnbanReason(e.target.value)}
                                placeholder="Optionaler Grund (für Logbuch)…"
                                className="bg-[#0B0B0C] border-white/10 text-white placeholder:text-[#52525b]"
                              />
                            </div>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={runUnban}
                                className="bg-[#F43F5E] hover:bg-[#e11d48] text-white"
                              >
                                {unbanLoading ? 'Entsperre…' : 'Ja, entsperren'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
